import { useEffect, useState } from "react";
import api, { apiError, formatEuro } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const emptyForm = { nome: "", descrizione: "", prezzo_combo: "", dish_ids: [], attivo: true };

export default function Combos() {
  const [combos, setCombos] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { user } = useAuth();
  const isAdmin = ["admin", "master"].includes(user?.role);

  const load = () => {
    api.get("/combos").then((r) => setCombos(r.data)).catch(() => {});
    api.get("/dishes").then((r) => setDishes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ nome: c.nome, descrizione: c.descrizione, prezzo_combo: String(c.prezzo_combo), dish_ids: c.dish_ids, attivo: c.attivo });
    setOpen(true);
  };

  const toggleDish = (id) =>
    setForm((f) => ({ ...f, dish_ids: f.dish_ids.includes(id) ? f.dish_ids.filter((d) => d !== id) : [...f.dish_ids, id] }));

  const totaleSelezionati = dishes.filter((d) => form.dish_ids.includes(d.id)).reduce((s, d) => s + d.prezzo, 0);

  const save = async () => {
    if (!form.nome) { toast.error("Il nome è obbligatorio"); return; }
    if (form.dish_ids.length < 2) { toast.error("Seleziona almeno 2 piatti per la combo"); return; }
    const payload = {
      nome: form.nome, descrizione: form.descrizione, prezzo_combo: parseFloat(form.prezzo_combo) || 0,
      dish_ids: form.dish_ids, attivo: form.attivo,
    };
    try {
      if (editing) { await api.put(`/combos/${editing.id}`, payload); toast.success("Combo aggiornata"); }
      else { await api.post("/combos", payload); toast.success("Combo creata"); }
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare "${c.nome}"?`)) return;
    try { await api.delete(`/combos/${c.id}`); toast.success("Combo eliminata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="combos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold">Combo</h1>
          <p className="text-[#6B7280] mt-1">Bundle di piatti a prezzo scontato.</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} data-testid="combo-add-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F] gap-2">
            <Plus size={18} weight="bold" /> Nuova combo
          </Button>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
        <Table data-testid="combos-table">
          <TableHeader className="bg-[#FAF9F6]">
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Piatti inclusi</TableHead>
              <TableHead>Prezzo pieno</TableHead>
              <TableHead>Prezzo combo</TableHead>
              <TableHead>Risparmio</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {combos.map((c) => (
              <TableRow key={c.id} className="hover:bg-stone-50" data-testid={`combo-row-${c.id}`}>
                <TableCell>
                  <p className="font-medium">{c.nome}</p>
                  <p className="text-xs text-[#6B7280] max-w-[220px] truncate">{c.descrizione}</p>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1.5">
                    {c.piatti.map((p) => <Badge key={p.id} variant="outline" className="rounded-full text-xs">{p.nome}</Badge>)}
                  </div>
                </TableCell>
                <TableCell className="text-[#6B7280] line-through">{formatEuro(c.prezzo_originale)}</TableCell>
                <TableCell className="font-semibold">{formatEuro(c.prezzo_combo)}</TableCell>
                <TableCell>
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-full">-{formatEuro(c.risparmio)}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`combo-edit-${c.id}`} className="hover:bg-stone-100"><PencilSimple size={18} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`combo-delete-${c.id}`} className="hover:bg-red-50 text-[#D32F2F]"><Trash size={18} /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="combo-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifica combo" : "Nuova combo"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} data-testid="combo-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Input value={form.descrizione} onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))} data-testid="combo-description-input" />
            </div>
            <div className="space-y-2">
              <Label>Piatti inclusi</Label>
              <div className="border border-[#E5E5E0] rounded-xl divide-y divide-[#E5E5E0] max-h-56 overflow-y-auto">
                {dishes.map((d) => (
                  <label key={d.id} className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 cursor-pointer">
                    <Checkbox checked={form.dish_ids.includes(d.id)} onCheckedChange={() => toggleDish(d.id)} data-testid={`combo-dish-checkbox-${d.id}`} />
                    <span className="flex-1 text-sm font-medium">{d.nome}</span>
                    <span className="text-sm text-[#6B7280]">{formatEuro(d.prezzo)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between bg-[#FAF9F6] border border-[#E5E5E0] rounded-xl px-4 py-3">
              <p className="text-sm text-[#6B7280]">Totale prezzo pieno: <span className="font-semibold text-[#1C1C1C]" data-testid="combo-total-preview">{formatEuro(totaleSelezionati)}</span></p>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Prezzo combo €</Label>
                <Input type="number" step="0.10" className="w-28 bg-white" value={form.prezzo_combo} onChange={(e) => setForm((f) => ({ ...f, prezzo_combo: e.target.value }))} data-testid="combo-price-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annulla</Button>
            <Button onClick={save} data-testid="combo-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
